/**
 * Manual source-PDF intake and source-blocker decision actions.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, getDashboardOperator } from "./dashboard-auth.js";
import { manualSourceIntakeUrl, manualSourceUploadUrl, sourceDecisionUrl } from "./dashboard-urls.js";

export function bindManualSourceIntakeActions(statusElement: HTMLElement, refresh: () => void): void {
  document.querySelectorAll<HTMLFormElement>("[data-source-manual-intake-form]").forEach((form) => {
    form.querySelector<HTMLButtonElement>("[data-source-manual-upload-action]")?.addEventListener("click", () => {
      void submitManualSourceUpload(form, statusElement);
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitManualSourceIntake(form, statusElement, refresh);
    });
  });
}

export async function submitManualSourceUpload(form: HTMLFormElement, statusElement: HTMLElement): Promise<void> {
  const county = form.dataset.county || "";
  const targetMonth = form.dataset.targetMonth || "";
  const file = form.querySelector<HTMLInputElement>("[data-source-manual-upload-file]")?.files?.[0];
  if (!file) {
    statusElement.textContent = `${county}: choose a PDF before uploading.`;
    return;
  }
  const upload = new FormData();
  upload.set("county", county);
  upload.set("targetMonth", targetMonth);
  upload.set("book", form.querySelector<HTMLInputElement>("[data-source-manual-book]")?.value.trim() || "");
  upload.set("page", form.querySelector<HTMLInputElement>("[data-source-manual-page]")?.value.trim() || "");
  upload.set("filedDate", form.querySelector<HTMLInputElement>("[data-source-manual-filed-date]")?.value.trim() || "");
  upload.set("documentType", form.querySelector<HTMLInputElement>("[data-source-manual-document-type]")?.value.trim() || "SUBSTITUTE TRUSTEE");
  upload.set("sourceUrl", form.querySelector<HTMLInputElement>("[data-source-manual-source-url]")?.value.trim() || "");
  upload.set("sourceDecisionId", form.dataset.sourceDecisionId || "");
  upload.set("sourceWorkTaskId", form.dataset.sourceWorkTaskId || "");
  upload.set("note", form.querySelector<HTMLInputElement>("[data-source-manual-note]")?.value.trim() || "");
  upload.set("file", file);
  statusElement.textContent = `${county}: uploading manual source PDF...`;
  try {
    const response = await authenticatedFetch(manualSourceUploadUrl, {
      method: "POST",
      body: upload
    });
    const body = (await response.json()) as { error?: string; artifactUri?: string; sha256?: string; sizeBytes?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    form.querySelector<HTMLInputElement>("[data-source-manual-artifact-uri]")!.value = body.artifactUri ?? "";
    form.querySelector<HTMLInputElement>("[data-source-manual-sha256]")!.value = body.sha256 ?? "";
    form.querySelector<HTMLInputElement>("[data-source-manual-size-bytes]")!.value = String(body.sizeBytes ?? "");
    form.dataset.uploaded = "true";
    statusElement.textContent = `${county}: uploaded PDF and filled R2 provenance. Create Lead when ready.`;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Manual source PDF upload failed.";
  }
}

export async function submitManualSourceIntake(form: HTMLFormElement, statusElement: HTMLElement, refresh: () => void): Promise<void> {
  const county = form.dataset.county || "";
  const targetMonth = form.dataset.targetMonth || "";
  const sourceDecisionId = form.dataset.sourceDecisionId || undefined;
  const sourceWorkTaskId = form.dataset.sourceWorkTaskId || undefined;
  const payload = {
    county,
    targetMonth,
    book: form.querySelector<HTMLInputElement>("[data-source-manual-book]")?.value.trim() || "",
    page: form.querySelector<HTMLInputElement>("[data-source-manual-page]")?.value.trim() || "",
    filedDate: form.querySelector<HTMLInputElement>("[data-source-manual-filed-date]")?.value.trim() || "",
    documentType: form.querySelector<HTMLInputElement>("[data-source-manual-document-type]")?.value.trim() || "SUBSTITUTE TRUSTEE",
    sourceUrl: form.querySelector<HTMLInputElement>("[data-source-manual-source-url]")?.value.trim() || undefined,
    artifactUri: form.querySelector<HTMLInputElement>("[data-source-manual-artifact-uri]")?.value.trim() || "",
    sha256: form.querySelector<HTMLInputElement>("[data-source-manual-sha256]")?.value.trim() || "",
    sizeBytes: Number(form.querySelector<HTMLInputElement>("[data-source-manual-size-bytes]")?.value || "0"),
    sourceDecisionId,
    sourceWorkTaskId,
    note: form.querySelector<HTMLInputElement>("[data-source-manual-note]")?.value.trim() || undefined
  };
  statusElement.textContent = `${county}: creating manual source PDF intake...`;
  try {
    const response = await authenticatedFetch(manualSourceIntakeUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as { error?: string; propertyLeadId?: string; sourceDocumentId?: string; createdTasks?: string[] };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${county}: created ${body.propertyLeadId ?? "lead"} from ${body.sourceDocumentId ?? "manual PDF"} with ${formatInteger(body.createdTasks?.length ?? 0)} starter tasks. Refreshing...`;
    form.dataset.recorded = "true";
    setTimeout(() => refresh(), 650);
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Manual source PDF intake failed.";
  }
}

export function bindSourceBlockerDecisionActions(statusElement: HTMLElement, refresh: () => void): void {
  document.querySelectorAll<HTMLFormElement>("[data-source-blocker-decision-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitSourceBlockerDecision(form, statusElement, refresh);
    });
  });
}

export async function submitSourceBlockerDecision(form: HTMLFormElement, statusElement: HTMLElement, refresh: () => void): Promise<void> {
  const county = form.dataset.county || "";
  const targetMonth = form.dataset.targetMonth || "";
  const blockerCode = form.dataset.blockerCode || "";
  const decision = form.dataset.decision || "";
  const status = form.querySelector<HTMLSelectElement>("[data-source-blocker-status]")?.value.trim() || "open";
  const assignedTo = form.querySelector<HTMLInputElement>("[data-source-blocker-assignee]")?.value.trim() || getDashboardOperator() || "";
  const note = form.querySelector<HTMLInputElement>("[data-source-blocker-note]")?.value.trim() || "";
  statusElement.textContent = `Recording ${labelize(decision)} decision for ${county}...`;
  try {
    const response = await authenticatedFetch(sourceDecisionUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        county,
        targetMonth,
        blockerCode,
        decision,
        status,
        assignedTo,
        note,
        nextAction: "review_typed_blocker"
      })
    });
    const body = (await response.json()) as { error?: string; action?: string; status?: string; assignedTo?: string | null };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${county} source decision recorded as ${labelize(body.status || status)}${body.assignedTo ? ` for ${body.assignedTo}` : ""}.`;
    form.dataset.recorded = "true";
    setTimeout(() => refresh(), 650);
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Source blocker decision failed.";
  }
}

